import React from "react";

/**
 * Official-look Google Play icon with the canonical four-color play
 * triangle (blue back-flap · green top · yellow apex · red bottom).
 *
 * Drop-in replacement for `<GooglePlayLogoIcon />` from Phosphor — same
 * visual size + className API, but with the brand-correct gradient fills
 * instead of a single-color glyph.
 *
 *   <GooglePlayColorIcon size={28} className="..." />
 */
export default function GooglePlayColorIcon({ size = 24, className = "" }) {
  // Unique gradient IDs per instance so multiple icons on the same page
  // don't share state when CSS animations or filters get applied.
  const id = React.useId().replace(/:/g, "");
  const idBlue = `gp-blue-${id}`;
  const idGreen = `gp-green-${id}`;
  const idYellow = `gp-yellow-${id}`;
  const idRed = `gp-red-${id}`;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <defs>
        {/* Blue — back flap (the "shaft" of the play arrow) */}
        <linearGradient id={idBlue} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00A0FF" />
          <stop offset="60%" stopColor="#00BFFF" />
          <stop offset="100%" stopColor="#0080FF" />
        </linearGradient>
        {/* Green — top fold */}
        <linearGradient id={idGreen} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00E673" />
          <stop offset="100%" stopColor="#00B765" />
        </linearGradient>
        {/* Yellow / orange — apex sliver */}
        <linearGradient id={idYellow} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFCE00" />
          <stop offset="100%" stopColor="#FFA200" />
        </linearGradient>
        {/* Red — bottom fold */}
        <linearGradient id={idRed} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FF3A44" />
          <stop offset="100%" stopColor="#C31162" />
        </linearGradient>
      </defs>

      {/* Blue — left back flap (A → B → M2 → M1) */}
      <path d="M3 3 L3 21 L13 12.6 L13 11.4 Z" fill={`url(#${idBlue})`} />
      {/* Green — top flap (A → M1 → C) */}
      <path d="M3 3 L13 11.4 L21 12 Z" fill={`url(#${idGreen})`} />
      {/* Yellow — right apex sliver between green & red (M1 → C → M2) */}
      <path d="M13 11.4 L21 12 L13 12.6 Z" fill={`url(#${idYellow})`} />
      {/* Red — bottom flap (B → C → M2) */}
      <path d="M3 21 L21 12 L13 12.6 Z" fill={`url(#${idRed})`} />
    </svg>
  );
}
